import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';

import { selectMemberProfile } from '@lib/members/data-access';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { distinctUntilChanged } from 'rxjs';

import { DEFAULT_PAGE_SIZE } from '@lib/commons/constants/request.const';

import { MemberProfile } from '@lib/core/models/member.interface';

import { SpinLoadingComponent } from '@lib/shared-ui/commons/spin-loading/spin-loading.component';
import { InfiniteScrollDirective } from '@lib/shared-ui/directives/infinite-scroll.directive';
import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

import { DocumentDetailsFormComponent } from './components/document-details-form/document-details-form.component';
import { DocumentDetailsComponent } from './components/document-details/document-details.component';
import { DocumentSearchComponent } from './components/document-search/document-search.component';
import { DocumentPreviewComponent } from './components/document-viewer/document-preview/document-preview.component';
import { AiDocumentsListComponent } from './components/documents-list/ai-documents-list/ai-documents-list.component';
import { DocumentsListComponent } from './components/documents-list/regular-documents-list/documents-list.component';
import { AiMedicalDocumentItem } from './core/models/document.interface';
import { DocumentDetailsState } from './core/models/medical-documents-state.interface';
import { MedicalDocumentsFacade } from './core/services/medical-documents.facade';
import { APPOINTMENTS_SIGNAL } from './core/tokens/appointments.token';
import { MD_LIB_CONFIG } from './core/tokens/medical-documents.tokens';

@Component({
  selector: 'md-medical-documents',
  standalone: true,
  imports: [
    DocumentSearchComponent,
    DocumentsListComponent,
    DocumentPreviewComponent,
    LoaderComponent,
    InfiniteScrollDirective,
    SpinLoadingComponent,
    TranslatePipe,
    ReactiveFormsModule,
    AiDocumentsListComponent,
    DocumentDetailsFormComponent,
    DocumentDetailsComponent,
  ],
  templateUrl: './medical-documents.component.html',
  styles: `
    :host {
      @apply h-full;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MedicalDocumentsComponent implements OnInit {
  private readonly mdConfig = inject(MD_LIB_CONFIG);
  private readonly store = inject(Store);
  private readonly facade = inject(MedicalDocumentsFacade);
  private readonly appointmentsSignal = inject(APPOINTMENTS_SIGNAL, { optional: true });
  private readonly destroyRef = inject(DestroyRef);

  protected readonly userAppointments = computed(() => (this.mdConfig.withAppointments && this.appointmentsSignal ? this.appointmentsSignal() : []));
  protected readonly memberProfile = computed(() => this.mdConfig.memberProfile || this.store.selectSignal<MemberProfile>(selectMemberProfile)());

  protected readonly isAiSearchFeatureEnabled = this.facade.isAiSearchFeatureEnabled;
  protected readonly isAiMedicalDataEnabled = this.facade.isAiMedicalDataEnabled;
  protected readonly isAiSearchChecked = this.facade.isAiSearchChecked;
  protected readonly aiSearchResponseMessage = this.facade.aiSearchResponseMessage;
  protected readonly isAiSearchLoading = this.facade.isAiSearchLoading;
  protected readonly documentsSearchTerm = this.facade.searchTerm;
  protected readonly documentsPageConfig = this.facade.pageConfig;
  protected readonly documents = this.facade.documents;
  protected readonly aiDocuments = this.facade.aiDocuments;
  protected readonly filteredAiDocuments = this.facade.filteredAiDocuments;
  protected readonly loadingDocumentsList = this.facade.listLoading;
  protected readonly currentAiDocument = this.facade.currentAiDocument;
  protected readonly uploadingDocuments = this.facade.uploadingDocuments;
  protected readonly detailsState = this.facade.detailsState;

  protected readonly displayDocuments = computed(() => (this.isAiSearchChecked() ? this.aiDocuments() : this.filteredAiDocuments()));

  // Feature flags computed from config
  protected readonly canEditDocuments = computed(() => this.mdConfig.documentsEditEnabled ?? true);
  protected readonly canUploadDocuments = computed(() => this.mdConfig.documentsUploadEnabled ?? true);
  protected readonly showAiContextSearch = computed(() => this.mdConfig.aiContextSearchEnabled ?? false);

  ngOnInit() {
    this.initFeatureFlags();
    if (this.mdConfig.withFirebase) this.facade.initFirebaseEvents();
    this.initSearchWatcher();

    if (this.isAiMedicalDataEnabled()) this.getAiDocumentsByUserId(this.mdConfig.userId);
    else this.fetchRegularDocuments();
  }

  protected getAiDocumentsByUserId(userId: string) {
    this.facade.loadAiDocuments(userId);
  }

  private initFeatureFlags() {
    const aiMedicalDataEnabled = this.mdConfig.aiMedicalDataEnabled || false;
    const aiContextSearchEnabled = this.mdConfig.aiContextSearchEnabled || false;

    this.facade.setAiFeatureFlags(aiMedicalDataEnabled, aiContextSearchEnabled);
  }

  protected fetchRegularDocuments() {
    const memberId = this.mdConfig.memberId || this.memberProfile()?.id;
    if (memberId) return this.facade.loadRegularDocuments(memberId, 1, DEFAULT_PAGE_SIZE, this.documentsSearchTerm());
  }

  protected fetchNextPage() {
    if (this.documentsPageConfig()?.hasNextPage) {
      const memberId = this.mdConfig.memberId || this.memberProfile()?.id;
      if (memberId) this.facade.loadRegularDocuments(memberId, this.documentsPageConfig().page + 1, this.documentsPageConfig().pageSize, this.documentsSearchTerm());
    }
  }

  protected closeDetails() {
    this.facade.closeDetails();
  }

  protected openEditMode() {
    this.facade.openEditMode();
  }

  protected onFormSubmitted(updatedDocument: AiMedicalDocumentItem) {
    if (updatedDocument) {
      const uploadingDocuments = this.uploadingDocuments();
      const isUploadingDocument = uploadingDocuments.some(doc => doc.uploadResponse?.id === updatedDocument.id);

      if (isUploadingDocument) this.facade.promoteUploadingToAiDocument(updatedDocument);
      else this.facade.updateAiDocument(updatedDocument);

      this.facade.openAiPreview(updatedDocument);
      this.facade.setDetailsState(DocumentDetailsState.Opened);
    }
  }

  protected onFormCancelled($event: boolean) {
    if ($event) this.facade.setDetailsState(DocumentDetailsState.Opened);
  }

  private initSearchWatcher() {
    this.facade.searchTerm$.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.isAiMedicalDataEnabled()) {
        this.facade.clearDocuments();
        this.fetchRegularDocuments();
        return;
      }

      const isContextSearchActive = this.isAiSearchFeatureEnabled() && this.isAiSearchChecked();
      const hasSearchTerm = this.documentsSearchTerm().length > 0;

      if (isContextSearchActive) {
        this.facade.setAiSearchResponseMessage('');
        if (!hasSearchTerm) this.getAiDocumentsByUserId(this.mdConfig.userId);
      } else this.getAiDocumentsByUserId(this.mdConfig.userId);
    });
  }
}
